import React, { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import { Warning } from "@mui/icons-material";
import { CircularProgress, Tooltip } from "@mui/material";

import _ from "lodash";
import { ControlledInputBase } from "src/components/Forms/ControlledInputs";
import { useAppDispatch } from "src/redux/hooks";
import {
  TOrderVariantError,
  removeOrderVariantError,
} from "src/redux/slices/ordering/orderSetSlice";
import { intValidation } from "src/utility/inputHelpers";
import { roundUp } from "src/utility/utilityFunctions";

import { getOrderVariantComplianceStatus } from "@features/compliance";
import { PaperTooltip } from "@features/ui";
import { OrderVariant } from "@models/OrderVariant";
import { html } from "@utils/tailwind";

import OrderVariantBudget from "./OrderVariantBudget";
import OrderVariantCostBreakdown from "./OrderVariantCostBreakdown";
import OrderVariantError from "./OrderVariantError";
import useSetOrderVariantQtyMutation from "./data/mutations/useSetOrderVariantQty";

const DisabledCell = html.div`absolute z-10 inset-0 bg-neutral-200 text-neutral-500 text-base flex justify-center items-center`;

const InputCell = ({
  inputRefs,
  orderVariant,
  handleKeyEvent,
  subjectToBudgeting,
}: {
  inputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  orderVariant: OrderVariant;
  handleKeyEvent: (evt: any) => void;
  subjectToBudgeting: boolean;
}) => {
  const dispatch = useAppDispatch();
  const orderVariantError: undefined | TOrderVariantError = useSelector(
    (state: any) => state.orderSet.orderVariantErrors[orderVariant.id],
    shallowEqual
  );
  const { control, resetField, getValues } = useFormContext();
  const setOrderVariantQtyMutation = useSetOrderVariantQtyMutation();

  // compliance status should not change over the lifecycle of this component
  const complianceStatus = React.useMemo(
    () => getOrderVariantComplianceStatus(orderVariant),
    // eslint-disable-next-line
    [orderVariant.id]
  );

  const isCompliant = complianceStatus !== "denied";
  const isPendingCompliance = complianceStatus === "pending";
  const isTacticOrderingBlocked = orderVariant.tacticOrderingBlocked === true;
  const budgetError = subjectToBudgeting && orderVariant.budgets.length === 0;

  const handleSaveRef = useRef<any>(null);

  handleSaveRef.current = () => {
    const fieldQty = getValues(orderVariant.id);
    // No one should order a billion of anything
    if (fieldQty.length > 9) {
      resetField(orderVariant.id);
      return;
    }
    const newQty = roundUp(fieldQty, orderVariant.qtyPerPack);

    if (+newQty !== +orderVariant.qty) {
      setOrderVariantQtyMutation.mutate(
        {
          id: orderVariant.id,
          qty: +newQty,
        },
        {
          onSuccess: ({ orderVariant }) => {
            resetField(orderVariant.id, { defaultValue: orderVariant.qty });
          },
        }
      );
    } else {
      dispatch(removeOrderVariantError({ id: orderVariant.id }));
    }
  };

  const clear = () => {
    resetField(orderVariant.id);
    dispatch(removeOrderVariantError({ id: orderVariant.id }));
  };

  const memoizedInput = React.useMemo(
    () =>
      isCompliant &&
      !isTacticOrderingBlocked &&
      !budgetError && (
        <ControlledInputBase
          id={orderVariant.id}
          ref={(el) => {
            inputRefs.current[orderVariant.id] = el;
          }}
          autoComplete="off"
          name={orderVariant.id}
          control={control}
          defaultValue="0"
          onFocus={(e) => {
            e.target.select();
            if (e.target.getBoundingClientRect().left < 300) {
              // Scroll to the start of the table
              e.target.scrollIntoView({
                inline: "center",
              });
            }
            window.addEventListener("keydown", handleKeyEvent);
          }}
          onBlur={() => {
            window.removeEventListener("keydown", handleKeyEvent);
            // If the form is reset from an outside event (e.g "Clear all errors" button),
            // this timeout ensures the form has the updated values before the save is triggered
            setTimeout(handleSaveRef.current, 100);
          }}
          rules={{
            ...intValidation,
            transformOnBlur: (v) =>
              !v || isNaN(+v) ? "0" : roundUp(v, orderVariant.qtyPerPack),
          }}
          className="absolute z-10 inset-0 px-3 pt-5 pb-2"
        />
      ),
    [
      isCompliant,
      isTacticOrderingBlocked,
      budgetError,
      orderVariant.id,
      handleKeyEvent,
      orderVariant.qtyPerPack,
      control,
      inputRefs,
    ]
  );

  if (!isCompliant) {
    return <DisabledCell>Not Compliant</DisabledCell>;
  }

  if (isTacticOrderingBlocked) {
    return (
      <PaperTooltip title="Item not supported for this brand.">
        <DisabledCell>Ordering Disabled</DisabledCell>
      </PaperTooltip>
    );
  }

  if (budgetError) {
    return (
      <PaperTooltip title="There is no budget for this State + Brand combination. If you feel this is in error, please contact Gallo Finance.">
        <DisabledCell className="cursor-help">No Budget</DisabledCell>
      </PaperTooltip>
    );
  }

  return (
    <>
      {memoizedInput}
      {/* Top text */}
      <OrderVariantBudget orderVariant={orderVariant} />
      {/* Right Text */}
      <div className="text-sm absolute z-10 text-neutral-500 top-[18px] right-3 flex gap-2 items-center">
        {setOrderVariantQtyMutation.isPending && <CircularProgress size={16} />}

        {isPendingCompliance && (
          <Tooltip title="Pending Compliance Rules">
            <Warning className="text-amber-400 text-lg" />
          </Tooltip>
        )}
        {orderVariantError && (
          <OrderVariantError
            error={orderVariantError}
            retry={handleSaveRef.current}
            clear={clear}
            isPending={setOrderVariantQtyMutation.isPending}
          />
        )}
        {!orderVariantError && orderVariant.qty > 0 && (
          <OrderVariantCostBreakdown orderVariant={orderVariant} />
        )}
      </div>
    </>
  );
};

export default React.memo(InputCell, (prev, next) => {
  return _.isEqual(prev.orderVariant, next.orderVariant);
});
